import React, { useEffect, useMemo, useState } from "react";

export interface AppTourStep {
  id: string;
  title: string;
  description: string;
  targetRef?: React.RefObject<any>;
}

interface SpotlightPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TooltipPosition {
  top: number;
  left: number;
  placement: "above" | "below";
  isMobile?: boolean;
}

interface AppTourProps {
  steps: AppTourStep[];
  isOpen: boolean;
  onClose: () => void;
}

export const modernStyles = {
  borderRadius: "6px",
  fieldHeight: "35px",
  focusShadow: "0 0 0 2px rgba(0, 120, 212, 0.2)",
  hoverBorderColor: "hsl(var(--primary))",
  activeBorderColor: "hsl(var(--primary))",
  labelColor: "hsl(var(--foreground))",
  placeholderColor: "hsl(var(--foreground))",
  spacing: "16px",
  descriptionColor: "hsl(var(--foreground))",
  backgroundColor: "hsl(var(--primary))",
  hoveredBackgroundColor: "hsl(var(--primary))",
};

const AppTour: React.FC<AppTourProps> = (props: AppTourProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [spotlight, setSpotlight] = useState<SpotlightPosition | null>(null);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition | null>(null);

  // Reset when reopened
  useEffect(() => {
    if (props.isOpen) {
      setCurrentIndex(0);
    }
  }, [props.isOpen]);

  const currentStep = useMemo(
    () =>
      props.steps.length > 0
        ? props.steps[Math.min(currentIndex, props.steps.length - 1)]
        : undefined,
    [props.steps, currentIndex],
  );

  // Compute spotlight + tooltip positions whenever step or layout changes
  useEffect(() => {
    if (!props.isOpen || !currentStep) {
      setSpotlight(null);
      setTooltipPos(null);
      return;
    }

    const target = currentStep.targetRef?.current;
    if (!target) {
      setSpotlight(null);
      setTooltipPos(null);
      return;
    }

    // On smaller screens, ensure the target element is brought into view once
    if (
      window.innerWidth <= 768 &&
      typeof target.scrollIntoView === "function"
    ) {
      target.scrollIntoView({
        behavior: "auto",
        block: "center",
        inline: "nearest",
      });
    }

    const computeLayout = () => {
      const rect = target.getBoundingClientRect();
      const margin = 8;

      const newSpotlight: SpotlightPosition = {
        top: rect.top - margin,
        left: rect.left - margin,
        width: rect.width + margin * 1.5,
        height: rect.height + margin * 1.5,
      };
      setSpotlight(newSpotlight);

      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const tooltipTop = viewportHeight / 2;
      const tooltipLeft = viewportWidth / 2;

      setTooltipPos({
        top: tooltipTop,
        left: tooltipLeft,
        placement: "below",
        isMobile: true,
      });
    };

    computeLayout();

    window.addEventListener("resize", computeLayout);
    window.addEventListener("scroll", computeLayout, true);

    return () => {
      window.removeEventListener("resize", computeLayout);
      window.removeEventListener("scroll", computeLayout, true);
    };
  }, [props.isOpen, currentStep, currentIndex, props.steps.length]);

  // Prevent background scrolling while the tour is active
  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [props.isOpen]);

  if (!props.isOpen || !currentStep) {
    return null;
  }

  const isLast = currentIndex === props.steps.length - 1;

  const handleNext = () => {
    if (isLast) {
      props.onClose();
    } else {
      setCurrentIndex((prev) => Math.min(prev + 1, props.steps.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  return (
    <div className="app-tour-overlay">
      {spotlight && (
        <>
          {/* Dimmed & blurred regions around the spotlight, leaving the center sharp */}
          <div
            className="app-tour-dim"
            style={{
              top: 0,
              left: 0,
              width: "100%",
              height: spotlight.top,
            }}
          />
          <div
            className="app-tour-dim"
            style={{
              top: spotlight.top,
              left: 0,
              width: spotlight.left,
              height: spotlight.height,
            }}
          />
          <div
            className="app-tour-dim"
            style={{
              top: spotlight.top,
              left: spotlight.left + spotlight.width,
              width: `calc(100% - ${spotlight.left + spotlight.width}px)`,
              height: spotlight.height,
            }}
          />
          <div
            className="app-tour-dim"
            style={{
              top: spotlight.top + spotlight.height,
              left: 0,
              width: "100%",
              height: `calc(100% - ${spotlight.top + spotlight.height}px)`,
            }}
          />

          {/* Highlight ring directly over the crisp spotlight area */}
          <div
            className="app-tour-spotlight"
            style={{
              top: spotlight.top,
              left: spotlight.left,
              width: spotlight.width,
              height: spotlight.height,
            }}
          />
        </>
      )}

      {tooltipPos && (
        <div
          className="app-tour-tooltip"
          style={{
            top: tooltipPos.top,
            left: tooltipPos.left,
            transform: tooltipPos.isMobile
              ? "translate(-50%, -50%)"
              : tooltipPos.placement === "above"
                ? "translateY(-100%)"
                : "translateY(0)",
          }}
        >
          <div className="app-tour-tooltip-card">
            <div
              className="app-tour-tooltip-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <div className="app-tour-tooltip-title">{currentStep.title}</div>
              <div className="app-tour-tooltip-step">
                Step {currentIndex + 1} of {props.steps.length}
              </div>
            </div>

            <div className="app-tour-tooltip-text" style={{ marginBottom: 12 }}>
              {currentStep.description}
            </div>

            <div
              className="app-tour-tooltip-actions"
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <button
                type="button"
                className="app-tour-button app-tour-button--secondary"
                onClick={props.onClose}
              >
                Skip tour
              </button>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="app-tour-button app-tour-button--secondary"
                  onClick={handleBack}
                  disabled={currentIndex === 0}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="app-tour-button app-tour-button--primary"
                  onClick={handleNext}
                >
                  {isLast ? "Finish" : "Next"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppTour;
